import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, Table, Alert, Spinner, Button, Modal, Form, Badge } from 'react-bootstrap'
import {
  useAdminDatumTags,
  useCreateAdminDatumTag,
  useUpdateAdminDatumTag,
  useDeleteAdminDatumTag
} from 'api/hooks/use-admin-datum-tags'
import { useAdminDatumUsers } from 'api/hooks/use-admin-datum'

const DatumTagsCard = ({ municipalityId }) => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [newTagName, setNewTagName] = useState('')
  const [editName, setEditName] = useState('')
  const [editReportIds, setEditReportIds] = useState([])
  const [editUserIds, setEditUserIds] = useState([])

  const { data: tagData, isLoading, error } = useAdminDatumTags({ municipalityId })
  const { data: datumUsers } = useAdminDatumUsers({ municipalityId })

  const createTag = useCreateAdminDatumTag({
    municipalityId,
    options: {
      onSuccess: () => {
        setShowCreateModal(false)
        setNewTagName('')
      }
    }
  })

  const updateTag = useUpdateAdminDatumTag({
    municipalityId,
    options: {
      onSuccess: () => {
        setShowEditModal(false)
        setEditingTag(null)
      }
    }
  })

  const deleteTag = useDeleteAdminDatumTag({ municipalityId })

  const tags = tagData?.tags || []
  const reports = tagData?.reports || []

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newTagName.trim()) return
    createTag.mutate({ name: newTagName.trim() })
  }

  const handleEdit = (tag) => {
    setEditingTag(tag)
    setEditName(tag.name)
    setEditReportIds(tag.report_ids || [])
    setEditUserIds(tag.user_ids || [])
    setShowEditModal(true)
  }

  const handleSaveEdit = (e) => {
    e.preventDefault()
    updateTag.mutate({
      tagId: editingTag.id,
      params: { name: editName, report_ids: editReportIds, user_ids: editUserIds }
    })
  }

  const handleDelete = (tagId) => {
    if (window.confirm('Delete this tag? Report and user assignments will be removed.')) {
      deleteTag.mutate(tagId)
    }
  }

  const toggleReportId = (reportId) => {
    setEditReportIds(prev =>
      prev.includes(reportId) ? prev.filter(id => id !== reportId) : [...prev, reportId]
    )
  }

  const toggleUserId = (userId) => {
    setEditUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    )
  }

  return (
    <>
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Report Tags</h5>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            Create Tag
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading && (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {error && (
            <div className="p-3">
              <Alert variant="danger" className="mb-0">Failed to load tags.</Alert>
            </div>
          )}

          {!isLoading && !error && (
            tags.length > 0 ? (
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Tag</th>
                    <th>Reports</th>
                    <th>Users</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <Badge bg="light">{tag.name}</Badge>
                      </td>
                      <td>
                        {(tag.report_ids || []).length === 0
                          ? <span className="text-muted">None</span>
                          : (tag.report_ids || []).map(rid => {
                              const report = reports.find(r => r.id === rid)
                              return (
                                <Badge key={rid} bg="info" className="me-1">
                                  {report ? report.name : rid.slice(0, 8)}
                                </Badge>
                              )
                            })
                        }
                      </td>
                      <td>
                        {(tag.user_ids || []).length === 0
                          ? <span className="text-muted">None</span>
                          : <Badge bg="primary">{tag.user_ids.length} user(s)</Badge>
                        }
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleEdit(tag)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(tag.id)}
                          disabled={deleteTag.isLoading}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-4">
                <Alert variant="info" className="mb-0">
                  No tags created yet. Tags let you control which reports each user can see.
                </Alert>
              </div>
            )
          )}
        </Card.Body>
      </Card>

      {/* Create Tag Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Tag</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Tag Name</Form.Label>
              <Form.Control
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g. Finance, Operations"
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light-primary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={createTag.isLoading}>
              {createTag.isLoading ? 'Creating...' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Tag Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Tag</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEdit}>
          <Modal.Body>
            <Form.Group className="mb-4">
              <Form.Label>Tag Name</Form.Label>
              <Form.Control
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </Form.Group>

            <h6 className="border-bottom pb-2 mb-3">Assign Reports</h6>
            {reports.length > 0 ? (
              <div className="mb-4" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {reports.map((report) => (
                  <Form.Check
                    key={report.id}
                    type="checkbox"
                    id={`report-${report.id}`}
                    label={report.name}
                    checked={editReportIds.includes(report.id)}
                    onChange={() => toggleReportId(report.id)}
                    className="mb-1"
                  />
                ))}
              </div>
            ) : (
              <Alert variant="warning" className="mb-4">
                No reports available. Ensure a Power BI workspace is configured.
              </Alert>
            )}

            <h6 className="border-bottom pb-2 mb-3">Assign Users</h6>
            {datumUsers && datumUsers.length > 0 ? (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {datumUsers.map((user) => (
                  <Form.Check
                    key={user.id}
                    type="checkbox"
                    id={`user-${user.id}`}
                    label={`${user.first_name} ${user.last_name} (${user.email})`}
                    checked={editUserIds.includes(user.id)}
                    onChange={() => toggleUserId(user.id)}
                    className="mb-1"
                  />
                ))}
              </div>
            ) : (
              <Alert variant="warning">
                No Datum users found. Add users first.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light-primary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={updateTag.isLoading}>
              {updateTag.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

DatumTagsCard.propTypes = {
  municipalityId: PropTypes.string.isRequired
}

export default DatumTagsCard
